// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createActionsHook,
	createStateHook,
} from '@atlassian/react-sweet-state';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	setUserProperties,
	getUserProperty,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { createErrorAnalytics } from '@atlassian/jira-polaris-lib-errors/src/controllers/index.tsx';
import { UnknownError } from '@atlassian/jira-polaris-lib-errors/src/common/utils/unknown-error/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { getOnboardingInformation } from '@atlassian/jira-polaris-remote-project/src/services/jira/onboarding-information/index.tsx';
import { ProjectTemplateEnum } from '@atlassian/jira-polaris-domain-project/src/types.tsx';
import type { ProjectContext } from '@atlassian/jira-providers-project-context/src/types.tsx';
import { BANNER_HEIGHT, BANNER_NAME } from '../../constants.tsx';
import type { State } from './types.tsx';

function getError(error: unknown): Error {
	if (error instanceof Error) {
		return error;
	}

	return new UnknownError('Retrieved non-standard error', error);
}

export const initialState: State = {
	isDismissed: false,
	height: BANNER_HEIGHT,
	shouldShow: false,
	isLoading: true,
};

export const actions = {
	onDismiss:
		(accountId: AccountId): Action<State> =>
		async ({ setState }) => {
			if (!accountId) {
				return;
			}

			// Update UI immediately - optimistic dismissal
			setState({
				isDismissed: true,
			});

			try {
				await setUserProperties(accountId, BANNER_NAME, 'false');
			} catch (error) {
				fireErrorAnalytics(
					createErrorAnalytics('polaris.jpd-blueprints-feedback.banner-dismissed', getError(error)),
				);
			}
		},

	fetchBannerVisibility:
		(accountId: AccountId, projectId?: ProjectContext['projectId']): Action<State> =>
		async ({ setState, getState }) => {
			if (!accountId) {
				return;
			}

			// Don't show the banner if projectId is not provided
			if (!projectId) {
				setState({
					shouldShow: false,
				});

				return;
			}

			try {
				const onboardingInformation = await getOnboardingInformation(String(projectId));

				// Show the banner only for BLUEPRINTS project
				if (onboardingInformation.initialProjectTemplate !== ProjectTemplateEnum.BLUEPRINTS) {
					setState({
						shouldShow: false,
						isLoading: false,
					});

					return;
				}

				const shouldShow = await getUserProperty<boolean>(accountId, BANNER_NAME);

				setState({
					shouldShow,
					isLoading: false,
				});
			} catch (error) {
				const { isDismissed } = getState();

				// Show the banner only if it has not been dismissed
				setState({
					shouldShow: !isDismissed,
					isLoading: false,
				});

				if (
					(error instanceof FetchError && error.statusCode === 404) ||
					isClientFetchError(error)
				) {
					return;
				}

				fireErrorAnalytics(
					createErrorAnalytics('polaris.jpd-blueprints-feedback.banner-dismissed', getError(error)),
				);
			}
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'PolarisBlueprintsFeedbackBanner',
	initialState,
	actions,
});

export const useJpdBlueprintsFeedbackBannerState = createStateHook(Store);
export const useJpdBlueprintsFeedbackBannerActions = createActionsHook(Store);
